import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  SlideContainer,
  SlideTitle,
} from '@/utils/export-pptx/components/slides/styles'
import { PptxObjectType } from '@/utils/export-pptx/constants'
import type { InsightOverviewSlide } from '@/utils/export-pptx/types/slide'

interface SlideProps {
  slide: InsightOverviewSlide
  onSlideReady?: (slide: InsightOverviewSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Text}>
        {formatMessage({
          id: 'insight_report:slide_insight_overview_title',
        })}
      </SlideTitle>

      <Period data-pptx-object={PptxObjectType.Text}>
        {formatMessage({
          id: 'insight_report:slide_insight_overview_period',
        })}
        ：{slide.payload.period[0]} - {slide.payload.period[1]}
      </Period>

      <Section>
        <Overview>
          <OverviewHeader>
            <Card data-pptx-object={PptxObjectType.Shape}>
              <CardTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({ id: 'autoreport:label_report_budget' })}
              </CardTitle>
              <CardContentEmphasize data-pptx-object={PptxObjectType.Text}>
                {slide.payload.budget}
              </CardContentEmphasize>
            </Card>
            <Card data-pptx-object={PptxObjectType.Shape}>
              <CardTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({ id: 'hashtag:billboard_engagement_count' })}
              </CardTitle>
              <CardContentEmphasize data-pptx-object={PptxObjectType.Text}>
                {slide.payload.engagementCount}
              </CardContentEmphasize>
            </Card>
            <Card data-pptx-object={PptxObjectType.Shape}>
              <CardTitle data-pptx-object={PptxObjectType.Text}>
                {formatMessage({
                  id: 'general:column_title_total_reach_count',
                })}
              </CardTitle>
              <CardContentEmphasize data-pptx-object={PptxObjectType.Text}>
                {slide.payload.reachCount}
              </CardContentEmphasize>
            </Card>
          </OverviewHeader>

          <Card data-pptx-object={PptxObjectType.Shape}>
            <CardTitle data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:field_name_cooperation_kol_combination',
              })}
            </CardTitle>
            <CardContent>
              <CardContentEmphasize data-pptx-object={PptxObjectType.Text}>
                {slide.payload.collaboration.kols.count}
                {formatMessage({
                  id: 'email:consult_received_field_kol_count_2',
                })}
              </CardContentEmphasize>

              <p data-pptx-object={PptxObjectType.Text}>
                {slide.payload.collaboration.kols.content}
              </p>
            </CardContent>
          </Card>

          <Card data-pptx-object={PptxObjectType.Shape}>
            <CardTitle data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:field_name_cooperation_content' })}
            </CardTitle>
            <CardContent>
              <CardContentCollaboration>
                {slide.payload.collaboration.posts.map((content, index) => (
                  <CardContentCollaborationItem key={index}>
                    {content.platform ? (
                      <PlatformImage
                        data-pptx-object={PptxObjectType.Image}
                        height={16}
                        platform={content.platform}
                        width={16}
                      />
                    ) : (
                      <span data-pptx-object={PptxObjectType.Text}>
                        {formatMessage({ id: 'general:platform_custom' })}
                        {formatMessage({ id: 'general:colon' })}
                      </span>
                    )}

                    <span data-pptx-object={PptxObjectType.Text}>
                      {content.content}
                    </span>
                  </CardContentCollaborationItem>
                ))}
              </CardContentCollaboration>
            </CardContent>
          </Card>
        </Overview>

        <Summary data-pptx-object={PptxObjectType.Text}>
          {slide.payload.summary}
        </Summary>
      </Section>

      <SubTitle>
        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'general:womm' })}
        </span>
        <SubTitleDescription data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'general:currency' })}：{slide.payload.currency}
        </SubTitleDescription>
      </SubTitle>

      <ValueAnalysis>
        {slide.payload.valueAnalysis.map((item, index) => (
          <ValueAnalysisItem key={index}>
            <ValueAnalysisItemTitle data-pptx-object={PptxObjectType.Text}>
              {item.title}
            </ValueAnalysisItemTitle>
            <ValueAnalysisItemContent data-pptx-object={PptxObjectType.Text}>
              {item.value}
            </ValueAnalysisItemContent>
          </ValueAnalysisItem>
        ))}
      </ValueAnalysis>
    </SlideContainer>
  )
}

const Period = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const Section = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  margin-bottom: 48px;
`

const Overview = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;
  gap: 16px;
`

const OverviewHeader = styled.div`
  display: flex;
  gap: 16px;
`

const Card = styled.div`
  flex-grow: 1;
  padding: 16px 12px;

  border: solid 1px ${({ theme }): string => theme.colors.border.border};
  border-radius: 6px;
  background: ${({ theme }): string => theme.colors.background.white};
`

const CardTitle = styled.p`
  font-size: 16px;
  font-weight: 400;

  color: ${({ theme }): string => theme.colors.text.secondary};
`

const CardContent = styled.p`
  margin-bottom: 0;

  font-size: 16px;
  font-weight: 400;

  color: ${({ theme }): string => theme.colors.text.secondary};
`

const CardContentEmphasize = styled(CardContent)`
  margin-right: 8px;

  font-size: 24px;
  font-weight: 700;
  font-family: Roboto;

  color: ${({ theme }): string => theme.colors.text.primary};
`

const CardContentCollaboration = styled.p`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  margin-bottom: 0;
`

const CardContentCollaborationItem = styled.span`
  display: flex;
  gap: 8px;
`

const Summary = styled.div`
  flex: 1;

  font-size: 18px;
  font-weight: 400;
  line-height: 160%;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const SubTitle = styled.p`
  display: flex;
  justify-content: space-between;

  margin-bottom: 48px;

  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const SubTitleDescription = styled.span`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`

const ValueAnalysis = styled.div`
  display: flex;
  gap: 16px;
`

const ValueAnalysisItem = styled.div`
  min-width: 148px;
`

const ValueAnalysisItemTitle = styled.p`
  margin-bottom: 12px;

  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const ValueAnalysisItemContent = styled.p`
  margin-bottom: 0;

  font-size: 20px;
  font-weight: 700;
  font-family: Roboto;
  color: ${({ theme }): string => theme.colors.text.primary};
`

export default SlideComponent
